import StudentDataFields from './StudentDataFields';
import { ProfileFieldSlot, ProfileFieldLabel } from '@/components/admin/PersonFields';
/* Everything about a person that only exists once they are a student:
   progress to graduation, enrolment, compliance, the money, their courses.

   Both admin pages render this. The student page passes the real record and
   its edit form; the admissions page passes the mirror it holds of that
   record, with `editing` false and no setters, because an admissions record
   reports the student's state rather than governing it.

   Values that can be worked out from the record are worked out here rather
   than threaded in as props, so the two callers only have to agree on what a
   student is -- not on how to summarise one. */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe2, GraduationCap, History, Wallet } from 'lucide-react';
import { ProfileSection, ProfileSectionPassthrough } from '@/components/admin/ProfileLayout';
import { inputClass } from './controls';
import {
  BillingSection,
  formatCurrency,
  courseBadgeKey,
  courseBadgeLabel,
  dateChangeLabel,
  displayText,
  formatDate,
  formatDateTime,
  graduationPercent,
  notificationStatuses,
  yesNoOptions,
} from './studentSectionHelpers';

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function num(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

export default function StudentSections({
  student,
  form,
  editing = false,
  meta = {},
  layoutController,
  // Courses live in their own collection. The student page passes the full
  // catalogue to resolve ids; the admissions mirror arrives already resolved.
  courses = [],
  intakeDateOptions = [],
  // Absent on the admissions page, which never edits a student from here.
  setText = () => () => {},
  setSelect = () => () => {},
  setGraduationStartDate = () => {},
  setGraduationEndDate = () => {},
  selectedProgramName = '',
  // The same linked student ledger is available on both detail pages.
  showLiveFinance = true,
  financeReadOnly = false,
  showGraduation = true,
  onTuitionPaymentsChanged,
}) {
  const record = student || {};

  const sStartDate = record.start_date;
  const sEndDate = record.end_date;
  const gradPct = graduationPercent(sStartDate, sEndDate);
  const sTuitionPaid = num(record.tuition_paid);
  const tuitionRemaining = Math.max(0, num(record.tuition_total) - sTuitionPaid);
  const gradeSnapshot = record.grade_snapshot && typeof record.grade_snapshot === 'object'
    ? record.grade_snapshot
    : {};
  const dateChangeHistory = [...safeList(record.date_change_history)].reverse();
  /* A student record stores course ids and the page resolves them against the
     catalogue. The admissions mirror instead carries the courses already
     resolved, under `courses`, so the names survive even if a course is later
     deleted. `record.courses` is the mirror's field and a student never has
     one, which is what keeps these two apart -- reading the catalogue prop as
     a fallback would list every course in the college. */
  const mirroredCourses = safeList(record.courses);
  const enrolledCourses = safeList(record.enrolled_courses).length
    ? safeList(record.enrolled_courses)
    : mirroredCourses.map((course) => course?.id).filter(Boolean);
  const courseMap = Object.fromEntries(
    [...safeList(courses), ...mirroredCourses]
      .filter((course) => course?.id)
      .map((course) => [course.id, course]),
  );

  return (
    <>
        {/* ── Graduation Progress ────────────────────────── */}
        {showGraduation && (
        <ProfileSection id="graduation" controller={layoutController}>
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
              <GraduationCap className="w-4 h-4 text-[#D4AF37]" /> Graduation Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {gradPct !== null ? (
              <>
                <div className="flex justify-between text-sm text-[#062B1A]/70 mb-1">
                  <span>{formatDate(sStartDate)}</span>
                  <span>{formatDate(sEndDate)}</span>
                </div>
                <Progress value={gradPct} className="h-4 [&>div]:bg-[#D4AF37]" />
                <p className="text-center text-sm font-medium text-[#062B1A]">{gradPct}% through program</p>
              </>
            ) : (
              <p className="text-sm text-[#062B1A]/50">Start and end dates are not set.</p>
            )}

            {editing ? (
              <div className="space-y-4 pt-2 border-t border-[rgba(6, 43, 26,0.08)]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ProfileFieldSlot label="Start Date" className="space-y-2">
                    <ProfileFieldLabel label="Start Date" />
                    <Select
                      value={form.start_date || undefined}
                      onValueChange={(value) => setGraduationStartDate(value, intakeDateOptions)}
                      disabled={!intakeDateOptions.length}
                    >
                      <SelectTrigger className={inputClass}>
                        <SelectValue placeholder={selectedProgramName ? 'Select intake start' : 'Select a program first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {intakeDateOptions.map((item) => (
                          <SelectItem key={item.start} value={item.start}>
                            {item.end
                              ? `${formatDate(item.start)} - ${formatDate(item.end)}`
                              : formatDate(item.start)}
                            {item.legacy ? ' (current)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!intakeDateOptions.length && (
                      <p className="text-xs text-[#062B1A]/50">
                        No intake start dates are configured for this program.
                      </p>
                    )}
                  </ProfileFieldSlot>
                  <ProfileFieldSlot label="End Date" className="space-y-2">
                    <ProfileFieldLabel label="End Date" />
                    <Input type="date" value={form.end_date} onChange={setGraduationEndDate} className={inputClass} />
                  </ProfileFieldSlot>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ProfileFieldSlot label="Graduation Ready" className="space-y-2">
                    <ProfileFieldLabel label="Graduation Ready" />
                    <Select value={form.graduation_ready} onValueChange={setSelect('graduation_ready')}>
                      <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {yesNoOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </ProfileFieldSlot>
                  <ProfileFieldSlot label="Notification Status" className="space-y-2">
                    <ProfileFieldLabel label="Notification Status" />
                    <Select value={form.graduation_notification_status} onValueChange={setSelect('graduation_notification_status')}>
                      <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {notificationStatuses.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </ProfileFieldSlot>
                </div>
                <ProfileFieldSlot label="Grade Summary Notes" className="space-y-2">
                  <ProfileFieldLabel label="Grade Summary Notes" />
                  <Textarea value={form.current_grade_summary} onChange={setText('current_grade_summary')} className={inputClass} rows={2} />
                </ProfileFieldSlot>
                <ProfileFieldSlot label="Last Term Notes" className="space-y-2">
                  <ProfileFieldLabel label="Last Term Notes" />
                  <Textarea value={form.last_term_notes} onChange={setText('last_term_notes')} className={inputClass} rows={2} />
                </ProfileFieldSlot>
              </div>
            ) : (
              <>
                {gradeSnapshot.average_percentage != null && (
                  <div className="pt-2 border-t border-[rgba(6, 43, 26,0.08)]">
                    <p className="text-sm text-[#062B1A]/70">
                      Current average: <span className="font-semibold text-[#062B1A]">{displayText(gradeSnapshot.average_percentage)}%</span>
                      <span className="ml-2 text-xs text-[#062B1A]/50">({displayText(gradeSnapshot.graded_items, '0')} items graded)</span>
                    </p>
                  </div>
                )}
                {record.graduation_ready && (
                  <Badge variant="outline" className="border-[rgba(34,197,94,0.25)] bg-[rgba(34,197,94,0.10)] text-[rgb(21,128,61)]">
                    Graduation ready
                  </Badge>
                )}
              </>
            )}
            <div className="pt-3 border-t border-[rgba(6, 43, 26,0.08)]">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#062B1A]">
                <History className="w-4 h-4 text-[#062B1A]/50" />
                Date Change History
              </div>
              {dateChangeHistory.length ? (
                <div className="space-y-2">
                  {dateChangeHistory.slice(0, 5).map((item, index) => (
                    <div key={item.id || `${item.field}-${item.changed_at}-${index}`} className="border-t border-[rgba(6,43,26,0.08)] pt-2 first:border-t-0 first:pt-0">
                      <div className="flex flex-col gap-1 text-xs text-[#062B1A]/60">
                        <span className="font-medium text-[#062B1A]">{dateChangeLabel(item)}</span>
                        <span>
                          {formatDate(item.from)} to {formatDate(item.to)}
                        </span>
                        <span>
                          {formatDateTime(item.changed_at)}
                          {item.changed_by_name ? ` by ${item.changed_by_name}` : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#062B1A]/50">No start or end date changes yet.</p>
              )}
            </div>
          </CardContent>
        </Card>
        </ProfileSection>
        )}

        {/* ── Enrollment ─────────────────────────────────── */}
        <ProfileSection id="compliance" controller={layoutController}>
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
              <Globe2 className="w-4 h-4 text-[#D4AF37]" /> {layoutController.sectionById.compliance?.title || 'Compliance'}
            </CardTitle>
          </CardHeader>
          <CardContent><StudentDataFields section="compliance" record={record} form={form} editing={editing} setText={setText} setSelect={setSelect} /></CardContent>
        </Card>
        </ProfileSection>

        {/* ── Financial Section ──────────────────────────── */}
        <ProfileSectionPassthrough>
        {!showLiveFinance && <ProfileSection id="billing" controller={layoutController}>
        {/* The ledger includes the totals; retain a summary for archived records. */}
        {!showLiveFinance && (
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
              <Wallet className="w-4 h-4 text-[#D4AF37]" /> {layoutController.sectionById.billing?.title || 'Billing'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!showLiveFinance && (
              <div className="grid grid-cols-3 gap-3 text-sm text-[#062B1A]">
                <div><p className="text-xs text-[#062B1A]/50">Tuition total</p>{formatCurrency(record.tuition_total)}</div>
                <div><p className="text-xs text-[#062B1A]/50">Paid</p>{formatCurrency(sTuitionPaid)}</div>
                <div><p className="text-xs text-[#062B1A]/50">Balance</p>{formatCurrency(tuitionRemaining)}</div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        </ProfileSection>}

        {/* Student Loan */}
        <ProfileSection id="loan" controller={layoutController}>
          <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
                <Wallet className="w-4 h-4 text-[#D4AF37]" /> {layoutController.sectionById.loan?.title || 'Student Loan'}
              </CardTitle>
            </CardHeader>
            <CardContent><StudentDataFields section="loan" record={record} form={form} editing={editing} setText={setText} setSelect={setSelect} /></CardContent>
          </Card>
        </ProfileSection>
        </ProfileSectionPassthrough>

        {showLiveFinance && (
        <ProfileSection id="billing" controller={layoutController}>
          <BillingSection student={record} readOnly={financeReadOnly} onChanged={onTuitionPaymentsChanged} />
        </ProfileSection>
        )}

        {/* ── Deposit Section ────────────────────────────── */}
        <ProfileSection id="deposit" controller={layoutController}>
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
              <Wallet className="w-4 h-4 text-[#D4AF37]" /> {layoutController.sectionById.deposit?.title || 'Deposit'}
            </CardTitle>
          </CardHeader>
          <CardContent><StudentDataFields section="deposit" record={record} form={form} editing={editing} setText={setText} setSelect={setSelect} /></CardContent>
        </Card>
        </ProfileSection>

        {/* ── Enrolled Courses (read-only info) ──────────── */}
        <ProfileSection id="courses" controller={layoutController}>
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
            <GraduationCap className="w-4 h-4 text-[#D4AF37]" /> {layoutController.sectionById.courses?.title || 'Enrolled Courses'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-1.5">
            {enrolledCourses.length > 0 ? (
              enrolledCourses.map((course, index) => (
                <Badge key={courseBadgeKey(course, index)} variant="outline" className="border-[rgba(6, 43, 26,0.12)] bg-white/70">
                  {courseBadgeLabel(course, courseMap)}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-[#062B1A]/45">No enrolled courses</span>
            )}
          </div>
        </CardContent>
      </Card>

        </ProfileSection>
    </>
  );
}
